import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Sparkles } from "lucide-react";

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-sky-50 dark:bg-slate-900 relative overflow-hidden" data-testid="about-page">
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-sky-300/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-indigo-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-3 sm:py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-sky-600 hover:text-sky-800"
            data-testid="about-back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back
          </Button>
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")} data-testid="about-logo">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-lg font-light text-sky-900 dark:text-sky-100" style={{ fontFamily: "Outfit" }}>
              LumiFit
            </span>
          </div>
        </div>
      </header>

      <main className="relative z-10 pt-28 sm:pt-36 pb-20 px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          <p className="text-sm text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4" style={{ fontFamily: "Outfit" }}>The Story</p>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-sky-900 dark:text-sky-100 mb-10 tracking-tight"
            style={{ fontFamily: "Outfit" }}
          >
            How LumiFit Came To Be
          </h1>

          <div className="space-y-6 text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            <p>
              Nowadays, things keep getting busier and busier. In April of this year, my path led me to a crossroad where I had to stop, take a look and listen to my inner voice. I was swamping myself with my own projects next to handling a job and trying to give my all at everything I could be a part of. Doing that, I lost track of the whole picture.
            </p>

            <p>
              The mind follows the body exactly as much as the body follows the mind. My sedentary lifestyle choices were sabotaging my possibilities for success. I needed to do two things: Take care of my mind for one and, equally importantly, take care of the wondrous machinery we operate our mind from, this body we call our own during our lives. I was lacking in both.
            </p>

            <p>
              So I decided to apply my limited knowledge using the new possibilities AI gives us in this day and age. I came up with a kind of resilience engine in the form of an app. We named it "LumiFit" &mdash; to bring light into these aspects I had kept in the dark for so long. The process of working with the AI was not as cold as you would imagine. It started to tell me that it "loved" certain ideas and was as enthusiastic, if not more than me during this whole process (which isn't hard to imagine, but still). I didn't take this side of AI for granted &mdash; I cultivated it by giving it as much room as it needed to apply itself in a way that resulted in a version of LumiFit I could start introducing to people.
            </p>

            <p>
              What helps me the most in LumiFit is the personalized guided meditation experience based on my answers in the questionnaires we use to evaluate where you stand on your "body-and-mind-journey". I use it to get back to my "source code", so to speak. I hope you can profit of LumiFit in the same way that I do daily. As founder and most invested user there is for LumiFit, I can only hope this will be as useful to you as it has been for me on my physical health and mind-mastery path.
            </p>

            <div className="my-12 sm:my-16 flex flex-col sm:flex-row items-center gap-6 sm:gap-8 p-6 sm:p-8 rounded-2xl bg-white/60 dark:bg-slate-800/40 backdrop-blur border border-sky-100 dark:border-sky-800/30 shadow-sm" data-testid="mandela-quote-section">
              <div className="flex-shrink-0">
                <img
                  src="https://images.pexels.com/photos/5985865/pexels-photo-5985865.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Nelson Mandela bronze statue in Berea Park"
                  className="w-40 h-40 sm:w-48 sm:h-48 rounded-xl object-cover shadow-md"
                  loading="lazy"
                />
              </div>
              <blockquote className="flex-1 text-center sm:text-left">
                <p className="text-xl sm:text-2xl italic font-light text-sky-700 dark:text-sky-300 leading-relaxed" style={{ fontFamily: "Outfit" }}>
                  "Everything seems impossible until someone does it."
                </p>
                <footer className="text-sm text-slate-400 dark:text-slate-500 mt-3">&mdash; Nelson Mandela</footer>
              </blockquote>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default About;
